import { Injectable, inject, signal, computed, effect, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UsersService } from '../api/api/users.service';
import { UserProfileDto } from '../api/model/userProfileDto';
import { UserCompanyDto } from '../api/model/userCompanyDto';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import { CompanyRole } from '../enums/company-role.enum';

const SELECTED_COMPANY_KEY = 'equity_plus_selected_company_id';

@Injectable({
  providedIn: 'root',
})
export class UserContextService {
  private usersService = inject(UsersService);
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);

  userProfile = signal<UserProfileDto | null>(null);
  userCompanies = signal<UserCompanyDto[]>([]);
  selectedCompany = signal<UserCompanyDto | null>(null);
  isLoadingContext = signal<boolean>(false);

  private lastSyncedToken: string | null = null;

  selectedCompanyId = computed(() => {
    return this.selectedCompany()?.companyId || localStorage.getItem(SELECTED_COMPANY_KEY) || '';
  });

  isGlobalAdmin = computed(() => {
    return this.userProfile()?.isGlobalAdmin ?? true;
  });

  currentRole = computed(() => {
    return this.selectedCompany()?.role ?? null;
  });

  isOperational = computed(() => {
    return this.isGlobalAdmin() || this.currentRole() === CompanyRole.Operational;
  });

  isReader = computed(() => {
    return !this.isGlobalAdmin() && this.currentRole() === CompanyRole.Reader;
  });

  isReadOnly = computed(() => {
    return this.isReader();
  });

  constructor() {
    // Automatically trigger sync and load companies ONCE per token change using untracked
    effect(
      () => {
        const token = this.authService.token();
        untracked(() => {
          if (token && token !== this.lastSyncedToken) {
            this.lastSyncedToken = token;
            this.syncAndLoadUserContext();
          } else if (!token) {
            this.lastSyncedToken = null;
            this.clearContext();
          }
        });
      },
      { allowSignalWrites: true }
    );
  }

  async syncAndLoadUserContext(): Promise<void> {
    if (this.isLoadingContext()) return;
    this.isLoadingContext.set(true);

    try {
      const firebaseUser = this.authService.user();
      const userName = firebaseUser?.displayName || firebaseUser?.email || 'Usuário';

      // 1. Sync User with Backend (POST /api/v1/users/sync)
      const profile = await firstValueFrom(this.usersService.syncUser({ name: userName }));
      if (profile) {
        this.userProfile.set(profile);
      } else {
        this.setDefaultAdminProfile();
      }

      // 2. Fetch User Companies (GET /api/v1/users/me/companies)
      const companies = await firstValueFrom(this.usersService.getCurrentUserCompanies());
      const companyList = companies || [];
      this.userCompanies.set(companyList);

      // 3. Determine active company selection
      const savedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);
      let foundCompany = companyList.find((c) => c.companyId === savedCompanyId);

      if (!foundCompany && companyList.length > 0) {
        foundCompany = companyList[0];
      }

      if (foundCompany) {
        this.selectCompany(foundCompany.companyId);
      } else {
        this.selectedCompany.set(null);
        localStorage.removeItem(SELECTED_COMPANY_KEY);
      }
    } catch (err) {
      console.error('Error synchronizing user context:', err);
      this.setDefaultAdminProfile();
    } finally {
      this.isLoadingContext.set(false);
    }
  }

  private setDefaultAdminProfile(): void {
    const firebaseUser = this.authService.user();
    this.userProfile.set({
      id: firebaseUser?.uid || 'local-admin',
      firebaseUid: firebaseUser?.uid || 'local-admin',
      email: firebaseUser?.email || '',
      name: firebaseUser?.displayName || firebaseUser?.email || 'Usuário Admin',
      isGlobalAdmin: true,
      isActive: true
    });
  }

  selectCompany(companyId: string): void {
    const found = this.userCompanies().find((c) => c.companyId === companyId);
    if (found) {
      this.selectedCompany.set(found);
      localStorage.setItem(SELECTED_COMPANY_KEY, companyId);
    }
  }

  async refreshCompanies(): Promise<void> {
    try {
      const companies = await firstValueFrom(this.usersService.getCurrentUserCompanies());
      const companyList = companies || [];
      this.userCompanies.set(companyList);

      const currentId = this.selectedCompanyId();
      let match = companyList.find((c) => c.companyId === currentId);

      if (!match && companyList.length > 0) {
        match = companyList[0];
      }

      if (match) {
        this.selectCompany(match.companyId);
      } else {
        this.selectedCompany.set(null);
        localStorage.removeItem(SELECTED_COMPANY_KEY);
      }
    } catch (err) {
      console.error('Error refreshing companies:', err);
    }
  }

  clearContext(): void {
    this.userProfile.set(null);
    this.userCompanies.set([]);
    this.selectedCompany.set(null);
    localStorage.removeItem(SELECTED_COMPANY_KEY);
  }
}
